import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Award, Calendar, MessageCircle, TrendingUp } from 'lucide-react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }

    fetchStats();
  }, [currentUser, navigate]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/user/stats`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <nav className="border-b-4 border-black bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20">
            <Button
              onClick={() => navigate('/profile')}
              variant="ghost"
              className="mr-4 border-2 border-black rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>My Stats & Badges</h1>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Reputation Score */}
        <div className="bg-gradient-to-r from-indigo-500 via-pink-400 to-lime-400 border-4 border-black rounded-3xl p-8 shadow-brutal-lg mb-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-white border-4 border-black rounded-full mb-4">
              <TrendingUp className="w-12 h-12 text-indigo-500" />
            </div>
            <h2 className="text-5xl font-black text-white mb-2" style={{fontFamily: 'Outfit'}}>
              {stats?.reputation_score || 0}
            </h2>
            <p className="text-white/90 text-lg font-semibold">Reputation Score</p>
          </div>
        </div>

        {/* Badges */}
        <div className="bg-white border-4 border-black rounded-3xl p-8 shadow-brutal-lg mb-8">
          <h3 className="text-2xl font-black text-slate-900 mb-6" style={{fontFamily: 'Outfit'}}>Earned Badges</h3>
          {stats?.badges && stats.badges.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stats.badges.map((badge, index) => (
                <div key={index} className="flex items-center gap-4 p-4 bg-lime-100 border-2 border-black rounded-xl">
                  <div className="text-4xl">{badge.icon}</div>
                  <div>
                    <div className="font-black text-lg" style={{fontFamily: 'Outfit'}}>{badge.name}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Award className="w-12 h-12 mx-auto mb-4 text-slate-400" />
              <p>No badges yet.</p>
            </div>
          )}
        </div>

        {/* Activity Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal text-center">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 text-indigo-500" />
            <div className="text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>{stats?.posts_count || 0}</div>
            <div className="text-sm font-semibold text-slate-600">Posts</div>
          </div>
          <div className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal text-center">
            <Calendar className="w-8 h-8 mx-auto mb-2 text-pink-500" />
            <div className="text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>{stats?.events_created || 0}</div>
            <div className="text-sm font-semibold text-slate-600">Events Created</div>
          </div>
          <div className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal text-center">
            <Users className="w-8 h-8 mx-auto mb-2 text-cyan-500" />
            <div className="text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>{stats?.events_attended || 0}</div>
            <div className="text-sm font-semibold text-slate-600">Events Attended</div>
          </div>
          <div className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal text-center">
            <Award className="w-8 h-8 mx-auto mb-2 text-lime-500" />
            <div className="text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>{stats?.servers_owned || 0}</div>
            <div className="text-sm font-semibold text-slate-600">Servers Owned</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Users({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}